import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Flame, MessageCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialQuery?: string;
}

type EntityHit = { kind: 'place'; id: string; title: string; district: string | null };
type DealHit = { kind: 'deal'; id: string; title: string; price: number | null };
type TalkHit = { kind: 'talk'; id: string; title: string };
type Hit = EntityHit | DealHit | TalkHit;

function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

function escapeIlike(s: string): string {
  // Strip pgrst-structural chars and wildcards; keep meaningful tokens only.
  return s.replace(/[%_,()*\\]/g, ' ').trim();
}

async function runSearch(q: string): Promise<Hit[]> {
  const safe = escapeIlike(q);
  if (safe.length < 2) return [];
  const like = `%${safe}%`;

  const [entitiesRes, dealsRes, talkRes] = await Promise.all([
    supabase
      .from('entities')
      .select('id, name, name_mn, location_district')
      .or(`name.ilike.${like},name_mn.ilike.${like}`)
      .eq('status', 'published')
      .limit(6),
    supabase
      .from('hot_deals')
      .select('id, title, price_mnt')
      .ilike('title', like)
      .eq('status', 'published')
      .limit(4),
    supabase
      .from('talk_posts')
      .select('id, title')
      .ilike('title', like)
      .eq('status', 'published')
      .limit(4),
  ]);

  const out: Hit[] = [];
  for (const r of entitiesRes.data ?? []) {
    out.push({
      kind: 'place',
      id: r.id as string,
      title: (r.name_mn as string) || (r.name as string),
      district: (r.location_district as string | null) ?? null,
    });
  }
  for (const r of dealsRes.data ?? []) {
    out.push({ kind: 'deal', id: r.id as string, title: r.title as string, price: (r.price_mnt as number | null) ?? null });
  }
  for (const r of talkRes.data ?? []) {
    out.push({ kind: 'talk', id: r.id as string, title: r.title as string });
  }
  return out;
}

export function UniversalSearchSheet({ open, onOpenChange, initialQuery = '' }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const debounced = useDebounced(query, 180);
  const navigate = useNavigate();

  useEffect(() => { if (open) setQuery(initialQuery); }, [open, initialQuery]);

  const { data: hits, isFetching } = useQuery({
    queryKey: ['universal-search', debounced],
    queryFn: () => runSearch(debounced),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const grouped = useMemo(() => {
    const places = (hits ?? []).filter((h) => h.kind === 'place') as EntityHit[];
    const deals = (hits ?? []).filter((h) => h.kind === 'deal') as DealHit[];
    const talk = (hits ?? []).filter((h) => h.kind === 'talk') as TalkHit[];
    return { places, deals, talk };
  }, [hits]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const fallbackTo = `/value/map?q=${encodeURIComponent(query.trim())}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Хайлт</DialogTitle>
        <DialogDescription className="sr-only">
          Газар, хоол, дүүрэг, урам болон постоор хайх
        </DialogDescription>
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Газар, хоол, дүүрэг, урам, пост…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim().length >= 2) go(fallbackTo);
            }}
          />
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {debounced.trim().length < 2 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              2-оос дээш үсэг бичээд хайна уу.
            </div>
          )}

          {debounced.trim().length >= 2 && !isFetching && (hits?.length ?? 0) === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground space-y-3">
              <p>"{debounced}" гэх илэрц олдсонгүй.</p>
              <button
                onClick={() => go(fallbackTo)}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Газрын зургаас үргэлжлүүлэн хайх <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}

          {grouped.places.length > 0 && (
            <Section title="Газар" icon={<MapPin className="h-3.5 w-3.5" />}>
              {grouped.places.map((p) => (
                <Row key={p.id} onClick={() => go(`/value/place/${p.id}`)}>
                  <MapPin className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    {p.district && <div className="text-xs text-muted-foreground truncate">{p.district}</div>}
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {grouped.deals.length > 0 && (
            <Section title="Урам" icon={<Flame className="h-3.5 w-3.5" />}>
              {grouped.deals.map((d) => (
                <Row key={d.id} onClick={() => go(`/uram/deals/${d.id}`)}>
                  <Flame className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    {d.price != null && (
                      <div className="text-xs text-primary">
                        {new Intl.NumberFormat('mn-MN').format(d.price)}₮
                      </div>
                    )}
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {grouped.talk.length > 0 && (
            <Section title="Яриа" icon={<MessageCircle className="h-3.5 w-3.5" />}>
              {grouped.talk.map((t) => (
                <Row key={t.id} onClick={() => go(`/talk/${t.id}`)}>
                  <MessageCircle className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {debounced.trim().length >= 2 && (hits?.length ?? 0) > 0 && (
            <button
              onClick={() => go(fallbackTo)}
              className="w-full mt-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 text-xs text-primary hover:bg-muted rounded-md"
            >
              Газрын зургаас бүх илэрц харах <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        {icon}
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted text-left"
    >
      {children}
    </button>
  );
}